import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  RefreshCw,
  Mic,
  CheckCircle,
  Loader2,
  BarChart3,
  Sparkles,
  Trash2,
  Stethoscope,
} from 'lucide-react';
import { apiFetch } from '../hooks/useFetch';
import PronunciationMappingCard from '../components/PronunciationMappingCard';

const KATIE_NATURAL_VOICE_ID = 'KKROuVYBO7tyUhA8R4FS';

const VoiceCloningAdmin = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [audioStats, setAudioStats] = useState(null);
  const [bulkProgress, setBulkProgress] = useState(null);
  const [working, setWorking] = useState(false);
  const [cleaningOrphans, setCleaningOrphans] = useState(false);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    const { data, error } = await apiFetch('/api/voice/config');
    if (!error) setConfig(data);
    setLoading(false);
  }, []);

  const fetchAudioStats = useCallback(async () => {
    const { data, error } = await apiFetch('/api/voice/audio-stats');
    if (!error) setAudioStats(data);
  }, []);

  useEffect(() => {
    fetchConfig();
    fetchAudioStats();
  }, [fetchConfig, fetchAudioStats]);

  const pollBulkProgress = async (jobId) => {
    const poll = async () => {
      try {
        const { data, error } = await apiFetch(`/api/voice/bulk-progress/${jobId}`);
        if (error) return;
        setBulkProgress(data);
        if (data.status === 'running' || data.status === 'starting') {
          setTimeout(poll, 2500);
        } else {
          fetchAudioStats();
          if (data.status === 'completed') toast.success(`Generated ${data.completed} audio files`);
          if (data.status === 'failed') toast.error(`Generation failed: ${data.error}`);
        }
      } catch (e) {
        // Polling errors are expected during transient network blips; log
        // at debug level so they're visible in dev without spamming prod.
        console.debug('VoiceCloningAdmin: poll error (will retry)', e);
      }
    };
    poll();
  };

  const handleFixAll = async () => {
    try {
      setWorking(true);
      toast.info('Verifying, syncing, and generating audio…');
      const { data, error } = await apiFetch('/api/voice/fix-all-audio', { method: 'POST' });
      if (error) {
        toast.error(error);
        return;
      }
      const r = data.results || {};
      const verifyMsg = `Cleared ${r.verify?.broken_cleared || 0} broken refs`;
      const syncMsg = `Linked ${r.sync?.newly_linked || 0} files`;
      if (r.generate?.job_id) {
        toast.success(`${verifyMsg}, ${syncMsg}. Generating ${r.generate.phrases_to_generate} phrases…`);
        pollBulkProgress(r.generate.job_id);
      } else {
        toast.success(`${verifyMsg}, ${syncMsg}. ${r.generate?.status === 'not_needed' ? 'All audio complete!' : r.generate?.reason || ''}`);
      }
      fetchAudioStats();
    } catch (e) {
      toast.error('Fix all audio failed');
    } finally {
      setWorking(false);
    }
  };

  const handleSync = async () => {
    try {
      setWorking(true);
      const { data, error } = await apiFetch('/api/voice/sync-audio-files', { method: 'POST' });
      if (error) {
        toast.error(error);
        return;
      }
      toast.success(`Synced: ${data.newly_linked || 0} audio files linked to phrases`);
      fetchAudioStats();
    } catch (e) {
      toast.error('Sync failed');
    } finally {
      setWorking(false);
    }
  };

  const handleCleanupOrphans = async () => {
    if (!window.confirm(
      'This will permanently delete audio files on disk that are not referenced by any phrase. Continue?'
    )) return;
    try {
      setCleaningOrphans(true);
      const { data, error } = await apiFetch('/api/voice/cleanup-orphans', { method: 'POST' });
      if (error) {
        toast.error(error);
        return;
      }
      toast.success(`Deleted ${data.deleted} orphan files. ${data.kept} working files kept.`);
      fetchAudioStats();
    } catch (e) {
      toast.error('Cleanup failed');
    } finally {
      setCleaningOrphans(false);
    }
  };

  const handleRegenMedicalAudio = async () => {
    if (!window.confirm(
      'This will clear cached audio for all medical phrases. The next time a user taps "Listen", audio is regenerated with correct abbreviation pronunciation (Hx → history, etc.). ElevenLabs credits are only spent on-demand, not now. Continue?'
    )) return;
    try {
      setWorking(true);
      const { data, error } = await apiFetch('/api/voice/regenerate-medical-audio', { method: 'POST' });
      if (error) { toast.error(error); return; }
      toast.success(
        `${data.flagged_for_regen} medical phrases flagged for regeneration (of ${data.total_medical_phrases} total).`,
        { duration: 6000 }
      );
      fetchAudioStats();
    } catch (e) {
      toast.error('Failed to flag medical audio for regeneration');
    } finally {
      setWorking(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading voice configuration…</span>
      </div>
    );
  }

  // Orphan = a file on disk with NO phrase reference. Must come from the
  // backend's referenced-set check, NOT from `on_disk - working` (which is
  // confused by phrase aliases that share the same file).
  const orphanCount = audioStats?.orphan_files_on_disk ?? 0;

  return (
    <div className="space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold">Audio Generation</h2>
          <p className="text-muted-foreground text-sm">
            All phrases use a single voice: <span className="font-medium text-foreground">Katie Natural Voice</span>
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => { fetchConfig(); fetchAudioStats(); }}
          data-testid="refresh-stats-btn"
        >
          <RefreshCw className="h-4 w-4 mr-1" />
          Refresh
        </Button>
      </div>

      {/* Voice in use */}
      <Card data-testid="active-voice-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Mic className="h-5 w-5 text-[#00E5CC]" />
            Active Voice
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
            <div>
              <p className="font-medium">Katie Natural Voice</p>
              <p className="text-xs text-muted-foreground font-mono mt-0.5">{KATIE_NATURAL_VOICE_ID}</p>
            </div>
            <span className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
              <CheckCircle className="h-4 w-4" />
              In use
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Audio Generation Stats */}
      {audioStats && (
        <Card data-testid="audio-stats-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-primary" />
              Audio Generation Progress
            </CardTitle>
            <CardDescription>
              {audioStats.coverage_percent}% coverage ({audioStats.phrases_with_audio} of {audioStats.total_phrases} phrases)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Progress value={audioStats.coverage_percent} className="h-3" />

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
              <div className="bg-green-50 dark:bg-green-950/30 p-3 rounded-lg">
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {audioStats.phrases_actually_working ?? audioStats.phrases_with_audio}
                </p>
                <p className="text-xs text-green-700 dark:text-green-300">Working Audio</p>
              </div>
              <div className="bg-amber-50 dark:bg-amber-950/30 p-3 rounded-lg">
                <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                  {audioStats.phrases_needing_audio ?? audioStats.phrases_without_audio}
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-300">Need Audio</p>
              </div>
              <div className="bg-red-50 dark:bg-red-950/30 p-3 rounded-lg">
                <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {audioStats.broken_references ?? 0}
                </p>
                <p className="text-xs text-red-700 dark:text-red-300">Broken Refs</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg" title="Unique audio files. Some files are shared between regional duplicates of the same phrase, so this can legitimately be lower than Working Audio.">
                <p className="text-2xl font-bold text-gray-600 dark:text-gray-300">
                  {audioStats.audio_files_on_disk}
                </p>
                <p className="text-xs text-gray-700 dark:text-gray-400">Unique Files on Disk</p>
              </div>
            </div>

            {/* Broken references warning */}
            {(audioStats.broken_references ?? 0) > 0 && audioStats.sample_broken?.length > 0 && (
              <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <p className="text-sm font-medium text-red-700 dark:text-red-400 mb-1">Broken Audio References:</p>
                <div className="space-y-1 text-xs text-red-600 dark:text-red-300">
                  {audioStats.sample_broken.map((b, i) => (
                    <div key={i}>#{b.id} &quot;{b.phrase}&quot; — missing: {b.missing_file}</div>
                  ))}
                </div>
              </div>
            )}

            {/* Fix All Audio */}
            <Button
              className="w-full bg-[#00E5CC] hover:bg-[#00C9B3] text-[#0d1f2d] font-medium text-sm py-3 h-auto whitespace-normal"
              data-testid="fix-all-audio-btn"
              disabled={working}
              onClick={handleFixAll}
            >
              {working ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin shrink-0" />
              ) : (
                <Sparkles className="h-4 w-4 mr-2 shrink-0" />
              )}
              <span>Fix All Audio (Verify + Sync + Generate)</span>
            </Button>

            {/* Sync only */}
            {audioStats.audio_files_on_disk > 0 &&
              audioStats.phrases_with_audio < audioStats.audio_files_on_disk && (
              <Button
                variant="outline"
                className="w-full font-medium text-sm py-3 h-auto whitespace-normal"
                data-testid="sync-audio-btn"
                disabled={working}
                onClick={handleSync}
              >
                <RefreshCw className="h-4 w-4 mr-2 shrink-0" />
                <span>Sync Only ({audioStats.audio_files_on_disk} Files on Disk)</span>
              </Button>
            )}

            {/* Cleanup Orphans */}
            {orphanCount > 0 && (
              <Button
                variant="outline"
                className="w-full font-medium text-sm py-3 h-auto whitespace-normal text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20 border-red-200 dark:border-red-900"
                data-testid="cleanup-orphans-btn"
                disabled={cleaningOrphans}
                onClick={handleCleanupOrphans}
              >
                {cleaningOrphans ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin shrink-0" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-2 shrink-0" />
                )}
                <span>Delete {orphanCount} Orphan File{orphanCount !== 1 ? 's' : ''} (Files on Disk Without Phrase Reference)</span>
              </Button>
            )}

            {/* Bulk progress */}
            {bulkProgress && (bulkProgress.status === 'running' || bulkProgress.status === 'starting') && (
              <div className="p-3 rounded-lg border bg-muted/30">
                <p className="text-sm font-medium mb-1">
                  {bulkProgress.status === 'starting' ? 'Starting…' : 'Generating audio…'}
                </p>
                <Progress
                  value={bulkProgress.total ? (bulkProgress.completed / bulkProgress.total) * 100 : 0}
                  className="h-2"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {bulkProgress.completed} / {bulkProgress.total}
                  {bulkProgress.current_phrase && <> — &quot;{bulkProgress.current_phrase}&quot;</>}
                </p>
              </div>
            )}

            {/* Category breakdown */}
            {audioStats.categories && audioStats.categories.some(c => c.with_audio < c.total) && (
              <div className="mt-4">
                <p className="text-sm font-medium mb-2">Categories Needing Audio:</p>
                <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
                  {audioStats.categories
                    .filter(c => c.with_audio < c.total)
                    .sort((a, b) => (b.total - b.with_audio) - (a.total - a.with_audio))
                    .slice(0, 10)
                    .map((cat) => (
                      <div
                        key={cat.name}
                        className="flex items-center justify-between text-xs p-2 rounded bg-muted/40"
                      >
                        <span className="capitalize">{cat.name}</span>
                        <span className="text-muted-foreground">
                          {cat.with_audio} / {cat.total}
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Pronunciation Mapping */}
      <PronunciationMappingCard />

      {/* Regenerate Medical Audio (applies Hx → history expansion) */}
      <Card data-testid="regen-medical-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Stethoscope className="h-5 w-5 text-red-500" />
            Medical Audio Pronunciation
          </CardTitle>
          <CardDescription>
            If you've updated the medical abbreviation expansion list (e.g.,
            Hx → history, HTN → hypertension), clear cached audio for all medical
            phrases so the next "Listen" tap regenerates with correct pronunciation.
            ElevenLabs credits are only spent on demand.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRegenMedicalAudio}
            disabled={working}
            data-testid="regen-medical-audio-btn"
          >
            {working ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Flagging…</>
            ) : (
              <><RefreshCw className="h-4 w-4 mr-2" /> Flag medical phrases for regen</>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* ElevenLabs status */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">ElevenLabs Status</CardTitle>
        </CardHeader>
        <CardContent>
          {config?.elevenlabs_configured ? (
            <p className="text-sm text-green-600 dark:text-green-400 flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              ElevenLabs API is configured
            </p>
          ) : (
            <p className="text-sm text-amber-600 dark:text-amber-400">
              ElevenLabs API key not configured. Audio generation is disabled.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VoiceCloningAdmin;
